import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";

import ApexCharts, { ApexOptions } from "apexcharts";

import { faArrowDown, faArrowUp } from "@fortawesome/pro-regular-svg-icons";

import { usePageTitle } from "@/utils/usePageTitle";
import { useGetKpiCoursesQuery, useGetKpiDashboardQuery } from "@/pages/Private/redux/kpi/kpi.api";

import { Icon, Information } from "@/components";

import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { ACCENT, CHART_SERIES, NEUTRAL, SEVERITY, SIGNAL } from "@/theme/tokens";

import { KpiPerScenario } from "./schema/kpi";

/*
 * §2.5 — data colours. Colour here encodes a value, not an intent, so this is the one
 * place allowed outside the brand hues. Everything is kept clear of #FF5A24: a series
 * that sits on the CTA's hue makes a data point read as a button.
 */
const CHART_COLORS = {
	primary: ACCENT.strong,
	success: SEVERITY.low,
	error: SIGNAL.danger,
	warning: SIGNAL.warning,
	muted: NEUTRAL[400],
	grid: NEUTRAL[50],
};

/** Ordered and distinguishable; see CHART_SERIES in src/theme/tokens.ts. */
const COURSE_PALETTE = [...CHART_SERIES];

export default function KpiDashboard() {
	const { t, i18n } = useTranslation();
	const ts = (key: string) => t(`kpiDashboard.${key}`);

	const PRESETS = [
		{ label: ts("7d"), days: 7 },
		{ label: ts("30d"), days: 30 },
		{ label: ts("90d"), days: 90 },
	];

	function getStartDate(days: number): string {
		const d = new Date();

		d.setDate(d.getDate() - days);
		const year = d.getFullYear();
		const month = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");

		return `${year}-${month}-${day}`;
	}

	function formatMs(ms: number | null): string {
		if (ms === null) {
			return "—";
		}

		const totalSecs = Math.floor(ms / 1000);
		const hours = Math.floor(totalSecs / 3600);
		const mins = Math.floor((totalSecs % 3600) / 60);
		const secs = totalSecs % 60;

		if (hours > 0) {
			return `${hours}h ${mins}m`;
		}

		if (mins > 0) {
			return `${mins}m ${secs}s`;
		}

		return `${secs}s`;
	}

	// Path deviation carries its own sign ("+2", "-4,4", "0"); BCM360-355 showed "+-4.91".
	function formatDeviation(value: number | null | undefined): string {
		if (value == null) {
			return "—";
		}

		return new Intl.NumberFormat(i18n.language?.startsWith("de") ? "de-DE" : "en-US", {
			signDisplay: "exceptZero",
			maximumFractionDigits: 2,
		}).format(value);
	}

	usePageTitle(ts("pageTitle"));

	function getTodayLocalDate(): string {
		const d = new Date();
		const year = d.getFullYear();
		const month = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");

		return `${year}-${month}-${day}`;
	}

	function formatTrendLabel(date: string, groupBy: "day" | "week"): string {
		if (groupBy === "day") {
			const [, month, day] = date.split("-");

			return `${day}.${month}`;
		}

		const parts = date.split("-");

		if (parts.length === 2) {
			const year = Number(parts[0]);
			const week = Number(parts[1]);
			const jan4 = new Date(year, 0, 4);
			const weekStart = new Date(jan4);

			weekStart.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (week - 1) * 7);
			const months = [
				"Jan",
				"Feb",
				"Mar",
				"Apr",
				"May",
				"Jun",
				"Jul",
				"Aug",
				"Sep",
				"Oct",
				"Nov",
				"Dec",
			];

			return `${months[weekStart.getMonth()]} ${weekStart.getDate()}`;
		}

		// Fallback: full date returned in week mode (stale cache) — just slice it
		return date.slice(5);
	}

	const [selectedPreset, setSelectedPreset] = useState<number | "custom">(30);
	const [groupBy, setGroupBy] = useState<"day" | "week">("day");

	const [customStart, setCustomStart] = useState("");
	const [customEnd, setCustomEnd] = useState("");

	const startDate = selectedPreset === "custom" ? customStart : getStartDate(selectedPreset);
	const endDate = selectedPreset === "custom" ? customEnd : getTodayLocalDate();
	const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);

	const { data: courses = [] } = useGetKpiCoursesQuery();
	const courseItems: DropdownWithCheckboxesItem[] = useMemo(
		() => courses.map((c) => ({ id: c.courseId, title: c.courseName })),
		[courses]
	);

	const sentCourseIds =
		selectedCourseIds.length === courseItems.length ? undefined : selectedCourseIds;

	const { data, isLoading, isFetching } = useGetKpiDashboardQuery(
		{ startDate, endDate, groupBy, courseIds: sentCourseIds },
		{ skip: selectedPreset === "custom" && (!customStart || !customEnd) }
	);

	const overall = data?.overall;
	const trend = data?.trend ?? [];
	const perScenario = data?.perScenario ?? [];
	const isSingleCourse = (sentCourseIds?.length ?? 0) === 1;

	const singleCourseName = isSingleCourse
		? courses.find((c) => c.courseId === sentCourseIds![0])?.courseName
		: undefined;

	useEffect(() => {
		if (courses.length > 0 && selectedCourseIds.length === 0) {
			setSelectedCourseIds(courses.map((c) => c.courseId));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [courses]);

	const handleCourseSelect = (selected: DropdownWithCheckboxesItem[]) => {
		const ids = selected.map((s) => Number(s.id));

		setSelectedCourseIds(ids);
	};

	function getTrend(current: number | null, previous: number | null, lowerIsBetter = true) {
		if (current === null || previous === null || previous === 0) {
			return null;
		}

		const pct = ((current - previous) / previous) * 100;

		if (pct === 0) {
			return null;
		}

		const isImprovement = lowerIsBetter ? pct < 0 : pct > 0;

		return { pct: Math.abs(pct).toFixed(1), isImprovement };
	}

	const courseNameWhenNotAllChosen = sentCourseIds
		? courses.filter((c) => sentCourseIds.includes(c.courseId)).map((c) => c.courseName)
		: undefined;
	const cards = [
		{
			label: ts("avgCompletionTime"),
			value: formatMs(overall?.avgCompletionTime ?? null),
			trend: getTrend(
				overall?.avgCompletionTime ?? null,
				overall?.previousAvgCompletionTime ?? null
			),
			info: ts("avgCompletionTimeInfo"),
		},
		{
			label: ts("avgResponseTime"),
			value: formatMs(overall?.avgResponseTime ?? null),
			trend: getTrend(overall?.avgResponseTime ?? null, overall?.previousAvgResponseTime ?? null),
			info: ts("avgResponseTimeInfo"),
		},
		{
			label: ts("avgPathDeviation"),
			value:
				overall?.avgPathDeviation != null
					? `${formatDeviation(overall.avgPathDeviation)} ${ts("stages")}`
					: "—",
			trend: getTrend(overall?.avgPathDeviation ?? null, overall?.previousAvgPathDeviation ?? null),
			info: ts("avgPathDeviationInfo"),
		},
		{
			label: ts("failedScenarios"),
			value: overall?.failedCount ?? 0,
			trend: getTrend(overall?.failedCount ?? null, overall?.previousFailedCount ?? null),
		},
	];

	type SortKey =
		| "scenarioName"
		| "totalScenarios"
		| "successCount"
		| "failedCount"
		| "indeterminateCount"
		| "avgCompletionTime"
		| "avgResponseTime"
		| "avgPathDeviation";

	const [sortKey, setSortKey] = useState<SortKey>("totalScenarios");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

	const handleSort = (key: SortKey) => {
		if (sortKey === key) {
			setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(key);
			setSortDir("desc");
		}
	};

	const sorted = [...perScenario].sort((a, b) => {
		const aVal = a[sortKey] ?? 0;
		const bVal = b[sortKey] ?? 0;

		if (aVal === bVal) {
			return 0;
		}

		return sortDir === "asc" ? (aVal > bVal ? 1 : -1) : aVal < bVal ? 1 : -1;
	});

	const SortIcon = ({ col }: { col: SortKey }) => {
		const isActive = sortKey === col;

		return (
			<span
				className={`ml-1 text-[10px] transition-opacity ${
					isActive
						? "text-accent-strong opacity-100"
						: "text-gray-300 opacity-0 group-hover:opacity-100"
				}`}
			>
				{isActive && sortDir === "asc" ? "▲" : "▼"}
			</span>
		);
	};

	const visibleTrend = useMemo(() => {
		if (sentCourseIds?.length === 1) {
			return trend.filter((t) => t.courseId === sentCourseIds[0]);
		}

		return trend;
	}, [trend, sentCourseIds]);

	const sortingOptions = [
		{ key: "scenarioName" as SortKey, label: ts("scenario"), align: "left" },
		{ key: "totalScenarios" as SortKey, label: ts("total"), align: "center" },
		{ key: "successCount" as SortKey, label: ts("success"), align: "center" },
		{ key: "failedCount" as SortKey, label: ts("failed"), align: "center" },
		{ key: "indeterminateCount" as SortKey, label: ts("indeterminate outcome"), align: "center" },
		{ key: "avgCompletionTime" as SortKey, label: ts("avgCompletionTime"), align: "center" },
		{ key: "avgResponseTime" as SortKey, label: ts("avgResponseTime"), align: "center" },
		{ key: "avgPathDeviation" as SortKey, label: ts("avgPathDeviation"), align: "center" },
	];

	type RunSortKey =
		| "date"
		| "outcome"
		| "avgCompletionTime"
		| "avgResponseTime"
		| "avgStagesTaken"
		| "avgOptimalPathLength"
		| "avgPathDeviation";

	const [runSortKey, setRunSortKey] = useState<RunSortKey>("date");
	const [runSortDir, setRunSortDir] = useState<"asc" | "desc">("desc");

	const handleRunSort = (key: RunSortKey) => {
		if (runSortKey === key) {
			setRunSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setRunSortKey(key);
			setRunSortDir("desc");
		}
	};

	const sortedRuns = useMemo(() => {
		return [...visibleTrend].sort((a, b) => {
			const aVal = a[runSortKey] ?? "";
			const bVal = b[runSortKey] ?? "";

			if (aVal === bVal) {
				return 0;
			}

			return runSortDir === "asc" ? (aVal > bVal ? 1 : -1) : aVal < bVal ? 1 : -1;
		});
	}, [runSortDir, runSortKey, visibleTrend]);

	const runSortingOptions: { key: RunSortKey; label: string; align: string }[] = [
		{ key: "date", label: ts("date"), align: "left" },
		{ key: "outcome", label: ts("outcome"), align: "center" },
		{ key: "avgCompletionTime", label: ts("completionTime"), align: "center" },
		{ key: "avgResponseTime", label: ts("responseTime"), align: "center" },
		{ key: "avgStagesTaken", label: ts("actualStages"), align: "center" },
		{ key: "avgOptimalPathLength", label: ts("optimalStages"), align: "center" },
		{ key: "avgPathDeviation", label: ts("pathDeviation"), align: "center" },
	];

	const trendSeries = useMemo(() => {
		const isSingleCourse = (sentCourseIds?.length ?? 0) === 1;

		if (isSingleCourse || visibleTrend.length === 0) {
			// 1. Deduplicate by date label, keeping the last entry per date
			const byDate = new Map<string, (typeof visibleTrend)[0]>();

			for (const t of visibleTrend) {
				const existing = byDate.get(t.date);

				if (!existing) {
					byDate.set(t.date, { ...t });
				} else {
					byDate.set(t.date, {
						...existing,
						avgCompletionTime: existing.avgCompletionTime ?? t.avgCompletionTime,
						avgResponseTime: existing.avgResponseTime ?? t.avgResponseTime,
						avgPathDeviation: existing.avgPathDeviation ?? t.avgPathDeviation,
						avgStagesTaken: existing.avgStagesTaken ?? t.avgStagesTaken,
						avgOptimalPathLength: existing.avgOptimalPathLength ?? t.avgOptimalPathLength,
					});
				}
			}

			const deduped = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));

			// 2. Build both series from the same deduped array (same length, same x values)
			const series = [
				{
					name: ts("avgCompletionTime"),
					data: deduped.map((t) => ({
						x: formatTrendLabel(t.date, groupBy),
						y: t.avgCompletionTime ?? null,
					})),
				},
				{
					name: ts("avgResponseTime"),
					data: deduped.map((t) => ({
						// ← no .filter() here
						x: formatTrendLabel(t.date, groupBy),
						y: t.avgResponseTime ?? null, // null renders as a gap, not a missing point
					})),
				},
			];

			return {
				series,
				colors: [CHART_COLORS.primary, CHART_COLORS.primary],
				dashArray: [0, 6],
				categories: [],
			};
		}

		const allDates = [...new Set(visibleTrend.map((t) => t.date))].sort();

		const byCourse = new Map<number, typeof trend>();

		for (const row of visibleTrend) {
			if (row.courseId == null) {
				continue;
			}

			if (!byCourse.has(row.courseId)) {
				byCourse.set(row.courseId, []);
			}

			byCourse.get(row.courseId)!.push(row);
		}

		const series: { name: string; data: { x: string; y: number | null }[] }[] = [];
		const colors: string[] = [];
		const dashArray: number[] = [];

		let i = 0;

		for (const [, rows] of byCourse) {
			const name = rows[0].courseName ?? String(rows[0].courseId);
			const color = COURSE_PALETTE[i % COURSE_PALETTE.length];
			const byDate = new Map(rows.map((r) => [r.date, r]));

			series.push({
				name: `${name} — ${ts("avgCompletionTime")}`,
				data: allDates.map((d) => ({
					x: formatTrendLabel(d, groupBy),
					y: byDate.get(d)?.avgCompletionTime ?? null,
				})),
			});
			colors.push(color);
			dashArray.push(0); // solid

			series.push({
				name: `${name} — ${ts("avgResponseTime")}`,
				data: allDates.map((d) => ({
					x: formatTrendLabel(d, groupBy),
					y: byDate.get(d)?.avgResponseTime ?? null,
				})),
			});
			colors.push(color);
			dashArray.push(6); // dashed

			i++;
		}

		return { series, colors, dashArray, categories: [] };
	}, [groupBy, sentCourseIds?.length, visibleTrend, ts]);

	const successTrend = visibleTrend.filter((t) => t.outcome?.toLowerCase() === "success");

	const courseEntries = useMemo(() => {
		if (isSingleCourse) {
			const color = trendSeries.colors[0];

			return [
				{ name: ts("avgCompletionTime"), color, seriesNames: [ts("avgCompletionTime")], dash: 0 },
				{ name: ts("avgResponseTime"), color, seriesNames: [ts("avgResponseTime")], dash: 6 },
			];
		}

		const seen = new Set<string>();
		const entries: { name: string; color: string; seriesNames: string[]; dash?: number }[] = [];

		trendSeries.series.forEach((s, i) => {
			const courseName = s.name.split(" — ")[0];

			if (!seen.has(courseName)) {
				seen.add(courseName);
				entries.push({
					name: courseName,
					color: trendSeries.colors[i],
					seriesNames: [
						`${courseName} — ${ts("avgCompletionTime")}`,
						`${courseName} — ${ts("avgResponseTime")}`,
					],
				});
			}
		});

		return entries;
	}, [trendSeries, ts, isSingleCourse]);

	return (
		<div className="p-6 space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm text-primary-gray mt-1">{ts("subtitle")}</p>
					{courseNameWhenNotAllChosen && (
						<div>
							{courseNameWhenNotAllChosen.map((name) => (
								<p key={name} className="text-xs text-accent-strong font-medium mt-0.5">
									{name}
								</p>
							))}
						</div>
					)}
				</div>
				<div className="flex items-start gap-3">
					<div className="flex flex-col gap-2">
						<div className="flex gap-1 bg-gray-600/5 rounded-lg p-1 justify-center">
							{PRESETS.map((p) => (
								<button
									key={p.label}
									className={`px-3 py-1 text-sm rounded-md transition-all ${
										selectedPreset === p.days
											? "bg-white shadow text-primary-gray font-medium"
											: "text-gray-500 hover:text-gray-700"
									}`}
									onClick={() => setSelectedPreset(p.days)}
								>
									{p.label}
								</button>
							))}
							<button
								className={`px-3 py-1 text-sm rounded-md transition-all ${
									selectedPreset === "custom"
										? "bg-white shadow text-primary-gray font-medium"
										: "text-gray-500 hover:text-gray-700"
								}`}
								onClick={() => setSelectedPreset("custom")}
							>
								{ts("custom")}
							</button>
						</div>

						<div
							className={`flex items-center gap-2 ${selectedPreset === "custom" ? "visible" : "invisible"}`}
						>
							<input
								className="text-sm w-[120px] border border-gray-200 rounded-md px-1.5 py-0.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-accent-strong"
								max={customEnd || endDate}
								type="date"
								value={customStart}
								onChange={(e) => setCustomStart(e.target.value)}
							/>
							<span className="text-gray-400 text-sm">→</span>
							<input
								className="text-sm w-[120px] border border-gray-200 rounded-md px-1.5 py-0.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-accent-strong"
								max={getTodayLocalDate()}
								min={customStart}
								type="date"
								value={customEnd}
								onChange={(e) => setCustomEnd(e.target.value)}
							/>
						</div>
					</div>
					<div className="flex gap-1 bg-gray-600/5 rounded-lg p-1">
						{(["day", "week"] as const).map((g) => (
							<button
								key={g}
								className={`px-3 py-1 text-sm rounded-md transition-all ${
									groupBy === g
										? "bg-white shadow text-primary-gray font-medium"
										: "text-gray-500 hover:text-gray-700"
								}`}
								onClick={() => setGroupBy(g)}
							>
								{ts(g)}
							</button>
						))}
						{courseItems.length > 1 && (
							<DropdownWithCheckboxes
								floating
								allLabel={ts("all")}
								classNameButton="!py-1 !text-sm !min-w-[220px] !truncate !flex !h-8 !px-2 !max-w-[100px]"
								data={courseItems}
								defaultValue={courseItems}
								handleSelect={handleCourseSelect}
								title="kpiDashboard.courses"
							/>
						)}
					</div>
				</div>
			</div>

			{isLoading || isFetching ? (
				<div className="flex justify-center py-20">
					<div className="w-8 h-8 border-4 border-accent-strong border-t-transparent rounded-full animate-spin" />
				</div>
			) : (
				<>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						{cards.map((card) => (
							<div
								key={card.label}
								className="bg-white rounded-xl border border-gray-200/40 shadow-sm p-5 space-y-1"
							>
								<div className="flex gap-2">
									<p className="text-xs font-medium text-primary-gray/80 uppercase tracking-wide">
										{card.label}
									</p>
									{card.info ? <Information information={card.info} /> : null}
								</div>
								<p className="text-2xl font-bold text-primary-gray">{card.value}</p>
								{card.trend && (
									<p
										className={`text-xs font-medium flex items-center gap-1 ${
											card.trend.isImprovement ? "text-status-success" : "text-status-error"
										}`}
									>
										<Icon
											className="w-3 h-3"
											icon={card.trend.isImprovement ? faArrowDown : faArrowUp}
										/>
										{card.trend.pct}%
										<span className="text-gray-700/70 font-normal">{ts("vsPreviousPeriod")}</span>
									</p>
								)}
							</div>
						))}
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 lg:col-span-2">
							<div className="mb-4 flex gap-2">
								<h2 className="text-sm font-semibold text-primary-gray ">{ts("trendTitle")}</h2>
								<Information information={ts("avgComparisonInfo")} />
							</div>
							{visibleTrend.length === 0 ? (
								<p className="text-sm text-gray-400 text-center py-10">{ts("noData")}</p>
							) : (
								<div className="[&_.apexcharts-legend-series:nth-child(even)]:hidden">
									<ReactApexChart
										key={`trend-${groupBy}-${(sentCourseIds ?? []).join(",") || "all"}-${visibleTrend.length}`}
										height={260}
										options={
											{
												chart: {
													id: "kpi-trend",
													type: "line",
													toolbar: { show: false },
													zoom: { enabled: false },
													animations: { enabled: false },
												},
												stroke: {
													curve: "smooth",
													width: 3,

													dashArray: trendSeries.dashArray,
												},
												legend: { show: false },
												markers: { size: 5 },
												colors: trendSeries.colors,
												xaxis: {
													type: "category" as const,
													labels: { style: { fontSize: "12px" } },
												},
												yaxis: {
													min: 0,

													labels: {
														formatter: (v: number) => {
															if (v == null || isNaN(v)) {
																return "—";
															}

															const totalSeconds = Math.floor(v / 1000);

															const hours = Math.floor(totalSeconds / 3600);
															const minutes = Math.floor((totalSeconds % 3600) / 60);
															const seconds = totalSeconds % 60;

															if (hours > 0) {
																return `${hours}h ${minutes}m`;
															}

															if (minutes > 0) {
																return `${minutes}m ${seconds}s`;
															}

															return `${seconds}s`;
														},
													},
												},
												tooltip: {
													shared: isSingleCourse,
													intersect: !isSingleCourse,
													y: {
														formatter: (v: number | null | undefined) =>
															v != null ? formatMs(v) : "—",
													},
												},

												grid: { borderColor: CHART_COLORS.grid },
											} as ApexOptions
										}
										series={trendSeries.series}
										type="line"
										width={"100%"}
									/>
								</div>
							)}
							{visibleTrend.length > 0 ? (
								<div className="relative flex w-full items-center mt-3">
									{/* CENTER: legend */}
									<div className="absolute left-1/2 -translate-x-1/2 flex flex-row gap-2">
										{courseEntries.map(({ name, color, seriesNames, dash }) => (
											<div
												key={name}
												className="flex items-center gap-2 cursor-pointer"
												onMouseEnter={() => {
													trendSeries.series.forEach((s) => {
														ApexCharts.exec(
															"kpi-trend",
															seriesNames.includes(s.name) ? "showSeries" : "hideSeries",
															s.name
														);
													});
												}}
												onMouseLeave={() => {
													trendSeries.series.forEach((s) =>
														ApexCharts.exec("kpi-trend", "showSeries", s.name)
													);
												}}
											>
												{dash !== undefined ? (
													<svg height="10" width="24">
														<line
															stroke={color}
															strokeDasharray={dash > 0 ? "4 3" : undefined}
															strokeWidth="2"
															x1="0"
															x2="24"
															y1="5"
															y2="5"
														/>
													</svg>
												) : (
													<svg height="10" width="10">
														<rect fill={color} height="10" width="10" />
													</svg>
												)}
												<span className="text-xs text-gray-500">{name}</span>
											</div>
										))}
									</div>

									{!isSingleCourse && (
										<div className="ml-auto flex flex-col gap-1 items-end">
											<div className="flex items-center gap-2">
												<svg height="10" width="24">
													<line stroke="#A3A19A" strokeWidth="2" x1="0" x2="24" y1="5" y2="5" />
												</svg>
												<span className="text-xs text-gray-500">{ts("avgCompletionTime")}</span>
											</div>

											<div className="flex items-center gap-2">
												<svg height="10" width="24">
													<line
														stroke="#A3A19A"
														strokeDasharray="4 3"
														strokeWidth="2"
														x1="0"
														x2="24"
														y1="5"
														y2="5"
													/>
												</svg>
												<span className="text-xs text-gray-500">{ts("avgResponseTime")}</span>
											</div>
										</div>
									)}
								</div>
							) : null}
						</div>

						<div
							className={`bg-white rounded-xl border border-gray-100 shadow-sm p-6 ${visibleTrend.length > 10 && isSingleCourse ? "lg:col-span-2" : ""}`}
						>
							<div className="mb-4  flex gap-2">
								<h2 className="text-sm font-semibold text-primary-gray ">
									{ts("timeComparisonTitle")}
								</h2>
								<Information information={ts("avgComparisonInfo")} />
							</div>
							{perScenario.length === 0 ? (
								<p className="text-sm text-gray-400 text-center py-10">{ts("noData")}</p>
							) : (
								<ReactApexChart
									key={`time-comparison-${groupBy}-${isSingleCourse ? visibleTrend.map((t) => t.date).join(",") : perScenario.map((s) => s.courseId).join(",")}`}
									height={260}
									options={
										{
											chart: { type: "bar", toolbar: { show: false } },
											plotOptions: {
												bar: {
													columnWidth: perScenario.length === 1 ? "20%" : "55%",
													borderRadius: 4,
												},
											},
											colors: [CHART_COLORS.primary, CHART_COLORS.success],
											xaxis: {
												categories: isSingleCourse
													? visibleTrend.map((t) => formatTrendLabel(t.date, groupBy))
													: perScenario.map((s) =>
															s.scenarioName.length > 15
																? s.scenarioName.slice(0, 15) + "..."
																: s.scenarioName
														),
												labels: { style: { fontSize: "11px" } },
											},
											yaxis: {
												...(trendSeries.series.length > 2
													? { logarithmic: true, logBase: 10 }
													: {}),
												labels: {
													formatter: (v: number) => {
														const totalMins = Math.round(v / 1000 / 60);
														const hours = Math.floor(totalMins / 60);
														const mins = totalMins % 60;

														return hours > 0 ? `${hours}h ${mins}m` : `${totalMins}m`;
													},
													style: { fontSize: "12px" },
												},
											},
											tooltip: { y: { formatter: (v: number) => formatMs(v) } },
											legend: { position: "top" },
											grid: { borderColor: CHART_COLORS.grid },
											dataLabels: { enabled: false },
										} as ApexOptions
									}
									series={[
										{
											name: isSingleCourse ? ts("completionTime") : ts("avgCompletionTime"),
											data: isSingleCourse
												? visibleTrend.map((t) => t.avgCompletionTime ?? 0)
												: perScenario.map((s) => s.avgCompletionTime ?? 0),
										},
										{
											name: isSingleCourse ? ts("responseTime") : ts("avgResponseTime"),
											data: isSingleCourse
												? visibleTrend.map((t) => t.avgResponseTime ?? 0)
												: perScenario.map((s) => s.avgResponseTime ?? 0),
										},
									]}
									type="bar"
									width={"100%"}
								/>
							)}
						</div>

						<div
							className={`bg-white rounded-xl border border-gray-100 shadow-sm p-6 ${isSingleCourse && visibleTrend.length > 10 ? "col-span-2" : ""}`}
						>
							<h2 className="text-sm font-semibold text-primary-gray mb-4">
								{ts("resultDistributionTitle")}
							</h2>
							{!overall || overall.totalScenarios === 0 ? (
								<p className="text-sm text-gray-400 text-center py-10">{ts("noData")}</p>
							) : (
								<ReactApexChart
									key={`result-distribution-${groupBy}-${overall?.totalScenarios}`}
									height={260}
									options={
										{
											chart: { type: "donut" },
											labels: [ts("success"), ts("failed"), ts("indeterminate outcome")],
											colors: [CHART_COLORS.success, CHART_COLORS.error, CHART_COLORS.warning],
											legend: { position: "bottom" },
											dataLabels: {
												formatter: (val: number) => `${Math.round(val)}%`,
											},
											tooltip: {
												y: { formatter: (v: number) => `${v} ${ts("runs")}` },
											},
											plotOptions: {
												pie: {
													donut: {
														size: "65%",
														labels: {
															show: true,
															total: {
																show: true,
																label: ts("total"),
																formatter: () => String(overall.totalScenarios),
															},
														},
													},
												},
											},
										} as ApexOptions
									}
									series={[overall.successCount, overall.failedCount, overall.indeterminateCount]}
									type="donut"
									width={"100%"}
								/>
							)}
						</div>

						{(isSingleCourse
							? successTrend.length > 0
							: perScenario.some((s) => s.successCount > 0)) && (
							<div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 lg:col-span-2">
								<div className="flex gap-3 mb-4">
									<h2 className="text-sm font-semibold text-primary-gray ">
										{ts("pathEfficiencyTitle")}
									</h2>
									<Information information={ts("avgPathDeviationInfo")} />
								</div>
								{perScenario.length === 0 ? (
									<p className="text-sm text-gray-400 text-center py-10">{ts("noData")}</p>
								) : (
									<ReactApexChart
										key={`path-efficiency-${groupBy}-${isSingleCourse ? successTrend.map((t) => t.date).join(",") : perScenario.map((s) => s.courseId).join(",")}`}
										height={260}
										options={
											{
												chart: { type: "bar", toolbar: { show: false } },
												plotOptions: { bar: { horizontal: false, borderRadius: 4 } },
												colors: [CHART_COLORS.primary, CHART_COLORS.muted],
												xaxis: {
													labels: { style: { fontSize: "11px" } },
												},
												yaxis: {
													labels: {
														formatter: (v: number) => `${Math.round(v)} ${ts("stages")}`,
														style: { fontSize: "11px" },
													},
												},
												tooltip: {
													y: {
														formatter: (v: number | null) =>
															v != null ? `${Math.round(v)} ${ts("stages")}` : "—",
													},
												},
												legend: { position: "top" },
												grid: { borderColor: CHART_COLORS.grid },
												dataLabels: { enabled: false },
											} as ApexOptions
										}
										series={[
											{
												name: ts("actualStages"),
												data: isSingleCourse
													? successTrend.map((t) => ({
															x: formatTrendLabel(t.date, groupBy),
															y: t.avgStagesTaken != null ? t.avgStagesTaken : null,
														}))
													: perScenario.map((s) => ({
															x:
																s.scenarioName.length > 15
																	? s.scenarioName.slice(0, 15) + "..."
																	: s.scenarioName,
															y: Math.round(s.avgStagesTaken || 0),
														})),
											},
											{
												name: ts("optimalStages"),
												data: isSingleCourse
													? successTrend.map((t) => ({
															x: formatTrendLabel(t.date, groupBy),
															y: t.avgOptimalPathLength != null ? t.avgOptimalPathLength : null,
														}))
													: perScenario.map((s) => ({
															x:
																s.scenarioName.length > 15
																	? s.scenarioName.slice(0, 15) + "..."
																	: s.scenarioName,
															y: Math.round(s.avgOptimalPathLength || 0),
														})),
											},
										]}
										type="bar"
										width={"100%"}
									/>
								)}
							</div>
						)}

						{!isSingleCourse && (
							<div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 lg:col-span-2">
								<h2 className="text-sm font-semibold text-primary-gray mb-4">
									{ts("outcomeDistributionTitle")}
								</h2>
								{perScenario.length === 0 ? (
									<p className="text-sm text-gray-400 text-center py-10">{ts("noData")}</p>
								) : (
									<ReactApexChart
										key={`outcome-distribution-${groupBy}-${perScenario.map((s) => s.courseId).join(",")}`}
										height={260}
										options={
											{
												chart: { type: "bar", stacked: true, toolbar: { show: false } },
												plotOptions: { bar: { horizontal: false, borderRadius: 4 } },
												colors: [CHART_COLORS.success, CHART_COLORS.error, CHART_COLORS.warning],
												xaxis: {
													categories: perScenario.map((s) =>
														s.scenarioName.length > 15
															? s.scenarioName.slice(0, 15) + "…"
															: s.scenarioName
													),
													labels: { style: { fontSize: "11px" } },
												},
												yaxis: {
													labels: { formatter: (v: number) => Math.round(v).toString() },
												},
												tooltip: { y: { formatter: (v: number) => `${v} ${ts("runs")}` } },
												legend: { position: "top" },
												grid: { borderColor: CHART_COLORS.grid },
												dataLabels: { enabled: false },
											} as ApexOptions
										}
										series={[
											{
												name: ts("success"),
												data: perScenario.map((s) => s.successCount),
											},
											{ name: ts("failed"), data: perScenario.map((s) => s.failedCount) },
											{
												name: ts("indeterminate outcome"),
												data: perScenario.map((s) => s.indeterminateCount ?? 0),
											},
										]}
										type="bar"
										width={"100%"}
									/>
								)}
							</div>
						)}
					</div>

					<div className="bg-white rounded-xl border border-gray-200/40 shadow-sm p-6">
						<h2 className="text-sm font-semibold text-primary-gray mb-4">
							{isSingleCourse ? singleCourseName : ts("perScenarioTitle")}
						</h2>

						{isSingleCourse ? (
							visibleTrend.length === 0 ? (
								<p className="text-sm text-primary-gray-lighter text-center py-10">
									{ts("noData")}
								</p>
							) : (
								<div className="overflow-x-auto">
									<table className="w-full text-sm">
										<thead>
											<tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-200/40">
												{runSortingOptions.map(({ key, label, align }) => (
													<th
														key={key}
														className={`group pr-4 py-2 cursor-pointer select-none transition-colors text-${align} ${
															runSortKey === key
																? "text-primary-gray font-semibold"
																: "text-primary-gray/50 hover:text-primary-gray"
														}`}
														onClick={() => handleRunSort(key)}
													>
														{label}
														<span
															className={`ml-1 text-[10px] transition-opacity ${
																runSortKey === key
																	? "text-accent-strong opacity-100"
																	: "text-gray-300 opacity-0 group-hover:opacity-100"
															}`}
														>
															{runSortKey === key && runSortDir === "asc" ? "▲" : "▼"}
														</span>
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{sortedRuns.map((run, i) => (
												<tr
													key={`${run.date}-${i}`}
													className="border-b border-gray-50 hover:bg-gray-50/20 transition-colors"
												>
													<td className="py-3 pr-4 text-left font-medium text-primary-gray/80">
														{new Date(run.date + "T00:00:00").toLocaleDateString("de-DE", {
															day: "2-digit",
															month: "2-digit",
															year: "numeric",
														})}
													</td>
													<td className="py-3 pr-4 text-center">
														{run.outcome ? (
															<span
																className={`text-xs font-medium px-2 py-0.5 rounded-full ${
																	run.outcome.toLowerCase() === "success"
																		? "bg-success-light text-text"
																		: run.outcome.toLowerCase() === "failed"
																			? "bg-error-light text-text"
																			: "bg-warning-light text-warning-ink"
																}`}
															>
																{ts(run.outcome.toLowerCase())}
															</span>
														) : (
															"—"
														)}
													</td>
													<td className="py-3 pr-4 text-center text-primary-gray/80">
														{formatMs(run.avgCompletionTime)}
													</td>
													<td className="py-3 pr-4 text-center text-primary-gray/80">
														{formatMs(run.avgResponseTime)}
													</td>
													<td className="py-3 pr-4 text-center text-primary-gray/80">
														{run.avgStagesTaken ?? "—"}
													</td>
													<td className="py-3 pr-4 text-center text-primary-gray/80">
														{run.avgOptimalPathLength ?? "—"}
													</td>
													<td className="py-3 text-center text-primary-gray/80">
														{formatDeviation(run.avgPathDeviation)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)
						) : perScenario.length === 0 ? (
							<p className="text-sm text-primary-gray-lighter text-center py-10">{ts("noData")}</p>
						) : (
							<div className="overflow-x-auto">
								<table className="w-full text-sm">
									<thead>
										<tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-200/40">
											{sortingOptions.map(({ key, label, align }) => (
												<th
													key={key}
													className={`group pr-4 py-2 cursor-pointer select-none transition-colors text-${align} ${
														sortKey === key
															? "text-primary-gray font-semibold"
															: "text-primary-gray/50 hover:text-primary-gray"
													}`}
													onClick={() => handleSort(key)}
												>
													{label}
													<SortIcon col={key} />
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{sorted.map((row: KpiPerScenario) => (
											<tr
												key={row.courseId}
												className="border-b border-gray-50 hover:bg-gray-50/20 transition-colors"
											>
												<td className="py-3 pr-4 text-left font-medium text-primary-gray/80">
													{row.scenarioName}
												</td>
												<td className="py-3 pr-4 text-center text-primary-gray/80">
													{row.totalScenarios}
												</td>
												<td className="py-3 pr-4 text-center text-severity-low">
													{row.totalScenarios > 0
														? `${Math.round((row.successCount / row.totalScenarios) * 100)}%`
														: "—"}
												</td>
												<td className="py-3 pr-4 text-center text-danger">
													{row.totalScenarios > 0
														? `${Math.round((row.failedCount / row.totalScenarios) * 100)}%`
														: "—"}
												</td>
												<td className="py-3 pr-4 text-center text-status-warning">
													{row.totalScenarios > 0
														? `${Math.round((row.indeterminateCount / row.totalScenarios) * 100)}%`
														: "—"}
												</td>
												<td className="py-3 pr-4 text-center text-primary-gray/80">
													{formatMs(row.avgCompletionTime)}
												</td>
												<td className="py-3 pr-4 text-center text-primary-gray/80">
													{formatMs(row.avgResponseTime)}
												</td>
												<td className="py-3 text-center text-primary-gray/80">
													{formatDeviation(row.avgPathDeviation)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}
